/**
 * Which quick-jobs the scheduler already runs for a chassis.
 *
 * Operators re-run "Collect MAC table" by hand without knowing a
 * nightly schedule already does it — wasted SSH sessions against a
 * device that is slow to answer, and a chassis lock held for nothing.
 * This maps job name → the schedule covering it so the Run-a-job list
 * can say so.
 *
 * **Fleet-wide schedules count.** A row with ``chassis_id === null``
 * runs against every chassis, so it covers this one. The per-chassis
 * endpoint (``GET /api/chassis/{cid}/schedules``) filters them out,
 * which is why callers should feed this the fleet list
 * (``GET /api/schedules``) and let it do the matching — otherwise a
 * job covered fleet-wide reads as "not automated".
 *
 * Disabled schedules never count as coverage.
 */

export type ScheduleLike = {
  job_name: string | null;
  cron_expr: string;
  interval_seconds: number | null;
  enabled: boolean;
  chassis_id: number | null;
  next_run_at?: string | null;
};

export type JobCoverage = {
  cadence: string;
  /** Whether the covering schedule is this chassis's or the fleet's. */
  scope: "chassis" | "fleet";
  nextRunAt: string | null;
};

/** Seconds → "6h" / "30m" / "45s" / "2d", largest whole unit that fits. */
export function formatInterval(seconds: number): string {
  if (seconds % 86_400 === 0) return `${seconds / 86_400}d`;
  if (seconds % 3_600 === 0) return `${seconds / 3_600}h`;
  if (seconds % 60 === 0) return `${seconds / 60}m`;
  return `${seconds}s`;
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Human cadence for a schedule.
 *
 * Handles the two shapes this product actually creates — a fixed
 * interval, and a plain daily/weekly cron — and falls back to the raw
 * expression rather than guessing. A wrong-but-confident "daily" on a
 * complex cron would be worse than showing the operator the cron.
 */
export function describeCadence(
  cronExpr: string,
  intervalSeconds: number | null,
): string {
  if (intervalSeconds && intervalSeconds > 0) {
    return `every ${formatInterval(intervalSeconds)}`;
  }
  const parts = cronExpr.trim().split(/\s+/);
  if (parts.length === 5) {
    const [min, hour, dom, mon, dow] = parts;
    const isNum = (v: string) => /^\d+$/.test(v);
    const at = () =>
      `${hour.padStart(2, "0")}:${min.padStart(2, "0")}`;
    if (isNum(min) && isNum(hour) && dom === "*" && mon === "*") {
      if (dow === "*") return `daily ${at()}`;
      if (isNum(dow) && DOW[Number(dow) % 7]) {
        return `${DOW[Number(dow) % 7]} ${at()}`;
      }
    }
  }
  return cronExpr;
}

/**
 * job_name → coverage, for every enabled schedule that runs against
 * this chassis. A chassis-scoped schedule wins over a fleet-wide one
 * for the same job: it is the more specific statement about this
 * chassis, and it is what the operator would edit.
 */
export function coverageByJob(
  schedules: ScheduleLike[] | undefined,
  chassisId: number,
): Map<string, JobCoverage> {
  const out = new Map<string, JobCoverage>();
  for (const s of schedules ?? []) {
    if (!s.enabled || !s.job_name) continue;
    const applies = s.chassis_id === chassisId || s.chassis_id === null;
    if (!applies) continue;

    const scope: JobCoverage["scope"] =
      s.chassis_id === null ? "fleet" : "chassis";
    const existing = out.get(s.job_name);
    if (existing && existing.scope === "chassis" && scope === "fleet") continue;

    out.set(s.job_name, {
      cadence: describeCadence(s.cron_expr, s.interval_seconds),
      scope,
      nextRunAt: s.next_run_at ?? null,
    });
  }
  return out;
}
